/**
 * What each code of Majik's records means. Keyed by the core's own types, so
 * a code added in src/core is a compile error here until it is described; the
 * short names are the screens' own label maps wherever one exists.
 */
import type { cleanerStatuses } from '@/core/cleaners/cleaner.ts'
import { type Shift, shiftLabels } from '@/core/cleaners/cleaner.ts'
import type {
  BillingMethod,
  billingCadences as billingCadenceList,
  PaymentTerms,
  ServiceStatus,
  TaxCode,
} from '@/core/clients/client.ts'
import {
  billingMethodLabels,
  paymentTermLabels,
  serviceStatusLabels,
} from '@/core/clients/client.ts'
import type {
  CommunicationStatus,
  channels as channelList,
  directions as directionList,
  PartyKind,
  Purpose,
} from '@/core/communications/communication.ts'
import { type Language, languageLabels } from '@/core/contacts/contact.ts'
import type { inspectionStatuses as inspectionStatusList } from '@/core/inspections/inspection.ts'
import type { InvoiceStatus, invoiceTypes as invoiceTypeList } from '@/core/invoices/invoice.ts'
import { type JobStatus, type recurringStatuses, shownStatusLabels } from '@/core/jobs/job.ts'
import {
  type OrderStatus,
  orderStatusLabels,
  type ProductType,
  type productCategories as productCategoryList,
  type productStatuses,
  productTypeLabels,
} from '@/core/orders/order.ts'
import { type Cadence, cadenceLabels } from '@/core/shared/cadence.ts'
import type { CodeDef } from '../types.ts'
import { codesOf } from './define.ts'

type Codes<V extends string> = Record<V, readonly [label: string, meaning: string]>
type Of<L extends readonly string[]> = L[number]

const taxCodes: Codes<TaxCode> = {
  tax: ['Tax', 'Sales tax is added to the invoice (NYC rate, in the business rules).'],
  non: ['Non-tax', 'No sales tax is added.'],
}

const paymentTerms: Codes<PaymentTerms> = {
  due_on_receipt: [
    paymentTermLabels.due_on_receipt,
    'Payment is due as soon as the invoice arrives.',
  ],
  net_10: [paymentTermLabels.net_10, 'Payment is due within 10 days of the invoice.'],
  net_30: [paymentTermLabels.net_30, 'Payment is due within 30 days of the invoice.'],
}

const billingMethods: Codes<BillingMethod> = {
  monthly_rate: [
    billingMethodLabels.monthly_rate,
    'A flat amount every month, from the client’s monthly items.',
  ],
  per_visit: [
    billingMethodLabels.per_visit,
    'Each visit is billed, rolled into one monthly invoice.',
  ],
  each_visit: [billingMethodLabels.each_visit, 'Each visit gets an invoice of its own.'],
}

const serviceStatuses: Codes<ServiceStatus> = {
  active: [serviceStatusLabels.active, 'Being cleaned now.'],
  paused: [serviceStatusLabels.paused, 'Cleaning is on hold for a while; expected to resume.'],
  unassigned: [serviceStatusLabels.unassigned, 'Wants cleaning but has no cleaner yet.'],
  inactive: [serviceStatusLabels.inactive, 'No longer cleaned. Kept with its history.'],
}

const cadences: Codes<Cadence> = {
  daily: [cadenceLabels.daily, 'Every day.'],
  weekly: [cadenceLabels.weekly, 'Once a week, on the same weekday.'],
  biweekly: [cadenceLabels.biweekly, 'Every two weeks, on the same weekday.'],
  every_3_weeks: [cadenceLabels.every_3_weeks, 'Every three weeks, on the same weekday.'],
  every_4_weeks: [cadenceLabels.every_4_weeks, 'Every four weeks, on the same weekday.'],
  monthly: [cadenceLabels.monthly, 'Once a month, on the same day of the month (or its last day).'],
  quarterly: [cadenceLabels.quarterly, 'Every three months, on the same day of the month.'],
}

const billingCadences: Codes<Of<typeof billingCadenceList>> = {
  monthly: ['Monthly', 'Next bill moves on a month after each monthly invoice.'],
  quarterly: ['Quarterly', 'Next bill moves on three months.'],
  semi_annual: ['Twice a year', 'Next bill moves on six months.'],
  yearly: ['Yearly', 'Next bill moves on a year.'],
}

const languages: Codes<Language> = {
  english: [languageLabels.english, 'Messages to them are written in English.'],
  french: [languageLabels.french, 'Messages to them are written in French.'],
  spanish: [languageLabels.spanish, 'Messages to them are written in Spanish.'],
}

/** For a list that is exactly active/inactive — a code added to it stops this compiling. */
const activeInactive = <L extends readonly ('active' | 'inactive')[]>(
  noun: string,
): Codes<Of<L>> => {
  const codes: Codes<'active' | 'inactive'> = {
    active: ['Active', `The ${noun} is in use.`],
    inactive: ['Inactive', `The ${noun} is kept but no longer in use.`],
  }
  return codes
}

const shifts: Codes<Shift> = {
  daytime: [`${shiftLabels.daytime}time`, 'Works during the day.'],
  nighttime: [`${shiftLabels.nighttime}time`, 'Works at night, after offices close.'],
}

const jobStatuses: Codes<JobStatus> = {
  draft: [
    shownStatusLabels.draft,
    'Not on yet: it never completes by itself, so it can’t reach Ready to bill.',
  ],
  scheduled: [
    shownStatusLabels.scheduled,
    'Booked. Completed automatically the night of the visit.',
  ],
  completed: [
    shownStatusLabels.completed,
    'Done and waiting to be billed (unless a monthly rate covers it).',
  ],
  billed: ['Billed', 'Done and on an invoice. Shown as Completed; its invoice shows in Billing.'],
  cancelled: [
    shownStatusLabels.cancelled,
    'Not happening. Leaves the default list and is never billed.',
  ],
}

const productCategories: Codes<Of<typeof productCategoryList>> = {
  frequently_ordered: ['Frequently ordered', 'Shown first in the order picker.'],
  special_order: ['Special order', 'Ordered in for a client when asked.'],
  product: ['Product', 'Any other product.'],
  service: ['Service', 'Work rather than a thing, e.g. a deep clean.'],
}

const productTypes: Codes<ProductType> = {
  service: [productTypeLabels.service, 'Work, not a thing. No stock.'],
  inventory: [
    productTypeLabels.inventory,
    'A supply kept in stock; delivering it lowers the stock.',
  ],
  non_inventory: [productTypeLabels.non_inventory, 'A thing not counted in stock, e.g. equipment.'],
}

const invoiceTypes: Codes<Of<typeof invoiceTypeList>> = {
  monthly: ['Monthly', 'One month of a client’s charges.'],
  one_off: ['One-off', 'A single visit or delivery, billed on its own.'],
}

const invoiceStatuses: Codes<InvoiceStatus> = {
  draft: ['Draft', 'Being put together; not sent.'],
  ready_to_send: ['Ready to send', 'Checked and waiting to go to QuickBooks.'],
  needs_attention: ['Needs attention', 'Something stopped it being sent; a person has to look.'],
  sent: ['Sent', 'Sent and waiting for payment. Overdue once older than the business rules allow.'],
  paid: ['Paid', 'Paid in full.'],
  void: ['Void', 'Cancelled without a payment. Its jobs can be billed again.'],
  uncollectible: ['Uncollectible', 'Written off: Majik doesn’t expect to be paid.'],
}

const orderStatuses: Codes<OrderStatus> = {
  draft: [orderStatusLabels.draft, 'Being put together.'],
  ready_for_delivery: [orderStatusLabels.ready_for_delivery, 'Packed and waiting to go out.'],
  out_for_delivery: [orderStatusLabels.out_for_delivery, 'On its way.'],
  delivered: [orderStatusLabels.delivered, 'At the site; its lines can now be billed.'],
  allocated: [
    orderStatusLabels.allocated,
    'Delivered, and every line is queued for the client’s next invoice.',
  ],
}

const inspectionStatuses: Codes<Of<typeof inspectionStatusList>> = {
  draft: ['Draft', 'Being put together; not sent.'],
  sent: ['Sent', 'Sent to the inspector (or recorded as “would send” before Go live).'],
}

const channels: Codes<Of<typeof channelList>> = {
  sms: ['Text', 'A text message.'],
  email: ['Email', 'An email.'],
  internal_note: ['Note', 'A note written by someone at Majik. Never sent.'],
  system: ['Event', 'Something that happened, recorded by the app or an automation.'],
}

const directions: Codes<Of<typeof directionList>> = {
  outbound: ['Sent', 'From Majik to someone.'],
  inbound: ['Received', 'From someone to Majik.'],
  internal: ['Internal', 'Kept inside Majik.'],
}

const purposes: Codes<Purpose> = {
  job_notification: ['Job notice', 'About a visit: made, changed, or the day-before notice.'],
  cleaner_schedule: ['Cleaner schedule', 'A cleaner’s list of jobs or weekly schedule.'],
  staff_broadcast: ['Staff broadcast', 'A message to many cleaners at once. May name no one.'],
  inspection: ['Inspection', 'An inspection request to an inspector.'],
  invoice: ['Invoice', 'An invoice or a payment reminder.'],
  payment: ['Payment', 'A payment arriving.'],
  customer_reminder: ['Customer reminder', 'The “it has been a while” email to a client.'],
  automation_error: ['Automation error', 'An automation that failed and said so.'],
  general: ['General', 'Anything else.'],
}

const communicationStatuses: Codes<CommunicationStatus> = {
  would_send: ['Would send', 'Written while Go live was off: it would have been sent, and wasn’t.'],
  sent: ['Sent', 'Handed to the text or email service.'],
  failed: ['Failed', 'The text or email service refused it.'],
  received: ['Received', 'Came in from outside.'],
  recorded: ['Recorded', 'Kept for the record; nothing was sent (notes and events).'],
}

const partyKinds: Codes<PartyKind> = {
  customer: ['Customer', 'A client’s point of contact.'],
  cleaner: ['Cleaner', 'A cleaner.'],
  billing: ['Billing contact', 'Whoever receives a client’s invoices.'],
  inspector: ['Inspector', 'An inspector.'],
  quickbooks: ['QuickBooks', 'QuickBooks itself.'],
  internal: ['Internal', 'Someone at Majik.'],
}

const CLIENTS = 'src/core/clients/client.ts'
const JOBS = 'src/core/jobs/job.ts'
const ORDERS = 'src/core/orders/order.ts'
const MESSAGES = 'src/core/communications/communication.ts'

export const majikCodeDefs: readonly CodeDef[] = [
  ...codesOf('contact', 'language', languages, 'src/core/contacts/contact.ts'),
  ...codesOf('client', 'taxCode', taxCodes, CLIENTS),
  ...codesOf('client', 'paymentTerms', paymentTerms, CLIENTS),
  ...codesOf('client', 'billingMethod', billingMethods, CLIENTS),
  ...codesOf('location', 'status', serviceStatuses, CLIENTS),
  ...codesOf('location', 'serviceFrequency', cadences, 'src/core/shared/cadence.ts'),
  ...codesOf('location', 'billingCadence', billingCadences, CLIENTS),
  ...codesOf(
    'cleaner',
    'status',
    activeInactive<typeof cleanerStatuses>('cleaner'),
    'src/core/cleaners/cleaner.ts',
  ),
  ...codesOf('cleaner', 'shift', shifts, 'src/core/cleaners/cleaner.ts'),
  ...codesOf(
    'recurringJob',
    'status',
    activeInactive<typeof recurringStatuses>('recurring job'),
    JOBS,
  ),
  ...codesOf('recurringJob', 'cadence', cadences, 'src/core/shared/cadence.ts'),
  ...codesOf('job', 'status', jobStatuses, JOBS),
  ...codesOf('product', 'category', productCategories, ORDERS),
  ...codesOf('product', 'type', productTypes, ORDERS),
  ...codesOf('product', 'taxCode', taxCodes, CLIENTS),
  ...codesOf('product', 'status', activeInactive<typeof productStatuses>('product'), ORDERS),
  ...codesOf('invoice', 'type', invoiceTypes, 'src/core/invoices/invoice.ts'),
  ...codesOf('invoice', 'status', invoiceStatuses, 'src/core/invoices/invoice.ts'),
  ...codesOf('invoice', 'taxCode', taxCodes, CLIENTS),
  ...codesOf('invoice', 'paymentTerms', paymentTerms, CLIENTS),
  ...codesOf('order', 'status', orderStatuses, ORDERS),
  ...codesOf('inspection', 'status', inspectionStatuses, 'src/core/inspections/inspection.ts'),
  ...codesOf('communication', 'channel', channels, MESSAGES),
  ...codesOf('communication', 'direction', directions, MESSAGES),
  ...codesOf('communication', 'purpose', purposes, MESSAGES),
  ...codesOf('communication', 'status', communicationStatuses, MESSAGES),
  ...codesOf('communication', 'partyKind', partyKinds, MESSAGES),
]
